import type { Film } from '@vista-digital/ocapi-types/v1'
import type { OccI18N } from '@vista-digital/occ-ui'
import type { BasePagesEnum, BaseSubPageKey } from './models/pagelist-models'
import type { NavigationService } from './navigation.service'

export abstract class UrlService<PagesEnum extends BasePagesEnum, SubPageKey extends BaseSubPageKey<PagesEnum>> {
    private i18n: OccI18N
    public setI18n(i18n: OccI18N) {
        this.i18n = i18n
    }

    protected abstract filmDetailsPage: keyof PagesEnum
    protected abstract cinemaPage: keyof PagesEnum
    protected abstract navigationService: NavigationService<PagesEnum, SubPageKey>

    public getFilmDetailsUrl(
        film: Film,
        siteId: string | undefined = undefined,
        extraParams: { [key: string]: string } = {}
    ): string {
        const filmParams = {
            filmName: this.urlFriendly(this.i18n?.translatable(film.title) ?? film.title.text),
            filmId: film.id,
        }
        const params = {
            ...extraParams,
            ...(siteId ? { ...filmParams, cinema: siteId } : filmParams),
        }

        return this.navigationService.getPageUrl(this.filmDetailsPage, params)
    }

    // public getShowtimeUrl(showtime: Showtime, extraParams: { [key: string]: string } = {}): string {
    //     const state = ticketingWorkflow.reloadStateFromStorage()

    //     const isAllocatedSeating = showtime?.isAllocatedSeating ?? true
    //     const destinationPage = state?.currentPage
    //         ? state.currentPage
    //         : isAllocatedSeating
    //           ? PagesEnum.SeatPicker
    //           : PagesEnum.TicketPicker

    //     return this.navigationService.getPageUrl(destinationPage, { showtimeId: showtime.id, ...extraParams })
    // }

    // Takes a string and returns it in an url-compatible format
    public urlFriendly(urlInput: string): string {
        // replaces white spaces with dashes
        urlInput = urlInput.replace(/\s+/g, '-')

        // Replace accent characters with normal ones
        urlInput = urlInput.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

        // Some replacements based on RequestHandlerSettings from Umbraco 13
        urlInput = urlInput.replace('+', 'plus')
        urlInput = urlInput.replace('*', 'star')
        urlInput = urlInput.replace('|', '-')

        // Replace special characters, except numbers, letters, hyphens and underscore
        urlInput = urlInput.replace(/[^a-zA-Z0-9-_]/g, '')

        // replaces all occurrences of two or more consecutive hyphens in a string with a single hyphen
        urlInput = urlInput.replace(/-{2,}/g, '-')

        urlInput = urlInput.toLowerCase()
        return urlInput
    }

    public isRouted(url: string) {
        return url.startsWith('/') || url.startsWith(document.location.origin)
    }

    public getRoutedUrl(url: string) {
        if (url.startsWith(document.location.origin)) return url.substring(document.location.origin.length)
        return url
    }

    public lastUrlSegment(url: string) {
        return url
            .split('/')
            .filter(i => i)
            .pop()
    }

    // Cinema
    public getCinemaUrl(cinemaName: string) {
        return this.navigationService.getPageUrl(this.cinemaPage, {
            cinemaName: cinemaName,
        })
    }
}
